import { fmtMoney, fmtMonthShort } from "../../lib/format";
import MoneyCell from "./MoneyCell";
import RatioCell from "./RatioCell";
import DragScrollContainer from "../DragScrollContainer";
import { currentMonthKey } from "../../lib/dateWindow";
import { altAmount, fmtAltValue, fxRound, hasAltCurrency, lockedRateOf } from "../../lib/currency";
import { Check, Lock, AlertTriangle } from "lucide-react";

// INVOICED grid — mirrors the forecast grid's rows (same stage order,
// including any Disbursements or Time-charge stage rows the user has added).
// Amounts are entered directly as dollars per stage per month.
//
// Each month header carries a DRAFT / CONFIRMED status toggle. Draft is the
// default when any value is typed; clicking the header confirms every scope
// row's invoiced amount for that month — the cells become read-only until
// reverted to draft. Confirmation is INVOICING-SIDE ONLY — it does NOT copy
// to or lock the forecast grid; the two grids stay fully independent.
//
// When an invoiced amount differs from that scope+month's FORECAST amount a
// red-delta pill appears on the invoiced cell showing the variance (e.g.
// "−$4,000 vs forecast"). It clears the moment the two match again.
// Mirrors the forecast grid: Contract value (SHARED with the forecast row —
// editing it here changes it there too) and a per-row Notes column that is
// INDEPENDENT of the forecast note (`invoiceNote`). Month cells accept a ratio
// of contract value ("5/10", "0.5") or raw dollars, exactly like the forecast.
export function InvoiceActuals({ months, scopes, project = null, invoicingConfirmed = {}, onScopeInvoiced, onScopeChange, onConfirmMonth, hideSub = false }) {
  // Same frozen-left widths as ScopeGrid so the two grids visually align.
  const W_STAGE = 190;
  const W_TYPE = 92;
  const W_CONTRACT = 104;
  const W_NOTE = 150;
  const FROZEN_W = W_STAGE + W_TYPE;
  const CUR = currentMonthKey();
  // The client is billed the GROSS total, so sub-consultant rows are excluded
  // from the invoiced grid when they live in their own deduction section.
  const rowIdxs = (scopes || []).map((_, i) => i)
    .filter((i) => !(hideSub && scopes[i].isSubConsultant));
  const isPast = (mo) => mo < CUR;
  // Invoicing records what has ALREADY been billed, so months later than the
  // as-at month are locked: no entry, no confirmation. Anything already
  // stored forward stays visible, flagged, and read-only.
  const isFuture = (mo) => mo > CUR;
  const futureWhy = (mo) =>
    `${fmtMonthShort(mo)} is later than the as-at month (${fmtMonthShort(CUR)}). Invoicing records what has already been billed, so it cannot be entered or confirmed here.`;
  const isConfirmed = (mo) => !!invoicingConfirmed?.[mo];
  const hasAny = (mo) => rowIdxs.some((i) => Number(scopes[i].invoiced?.[mo]) !== 0);
  const rowLabel = (s) =>
    s.isSubConsultant ? "Sub consultants" : (s.parentScopeId ? `└ ${s.subsetLabel || s.stage || "subset"}` : (s.stage || "—"));
  const feeTypeLabel = (s) => {
    if (s.isSubConsultant) return "pass-through";
    return s.feeType === "time_charge" ? "time charge" : "fixed";
  };
  const rowTotal = (inv) => months.reduce((t, mo) => t + (Number(inv?.[mo]) || 0), 0);
  const monthTotal = (mo) =>
    rowIdxs.reduce((t, i) => t + (Number(scopes[i].invoiced?.[mo]) || 0), 0);
  const grand = months.reduce((t, mo) => t + monthTotal(mo), 0);
  const contractTotal = rowIdxs.reduce((t, i) => t + (Number(scopes[i].contractValue) || 0), 0);

  // Red delta flag: invoiced ≠ forecast for that row+month. Uses a small
  // tolerance ($0.50) so rounding differences don't spuriously flag.
  const delta = (s, mo) => {
    const inv = Number(s.invoiced?.[mo] || 0);
    const fc  = Number(s.revenue?.[mo]  || 0);
    if (inv === 0 && fc === 0) return null;
    const d = inv - fc;
    if (Math.abs(d) < 0.5) return null;
    return d;
  };

  return (
    <div className="border border-[var(--rule)]" data-testid="invoice-actuals">
      <DragScrollContainer className="overflow-x-auto thin-scroll">
        <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-[var(--paper-soft)] eyebrow text-left px-3 py-2 border-b border-r border-[var(--rule)]" style={{ width: W_STAGE, minWidth: W_STAGE }}>
                <div className="text-[#7C3A06]">Invoiced</div>
                <div className="normal-case tracking-[0.06em] text-[10px] text-[var(--soft-fg)]">by stage · mirrors forecast rows</div>
              </th>
              <th className="sticky z-20 bg-[var(--paper-soft)] eyebrow text-left px-2 py-2 border-b border-r border-[var(--rule-strong)]" style={{ left: W_STAGE, width: W_TYPE, minWidth: W_TYPE }}>Type</th>
              <th className="px-2 py-2 eyebrow text-right border-b border-r border-[var(--rule)] bg-[var(--paper-soft)]" style={{ width: W_CONTRACT, minWidth: W_CONTRACT }}>
                <div>Contract value</div>
                <div className="normal-case tracking-[0.06em] text-[10px] text-[var(--soft-fg)]">shared with forecast</div>
              </th>
              <th className="px-2 py-2 eyebrow text-left border-b border-r border-[var(--rule)] bg-[var(--paper-soft)]" style={{ width: W_NOTE, minWidth: W_NOTE }}>
                <div>Notes</div>
                <div className="normal-case tracking-[0.06em] text-[10px] text-[var(--soft-fg)]">invoicing only</div>
              </th>
              {months.map((mo) => {
                const past = isPast(mo);
                const conf = isConfirmed(mo);
                const future = isFuture(mo);
                return (
                  <th
                    key={mo}
                    title={future ? futureWhy(mo) : undefined}
                    className={`px-2 py-2 eyebrow text-right border-b border-[var(--rule)] ${past ? "bg-[var(--paper-soft)]/70 text-[var(--muted-fg)]" : "text-[var(--soft-fg)]"} ${future ? "bg-[#FBF7EF] text-[var(--rule-strong)]" : ""} ${conf ? "border-b-2 border-b-[#2C4A38]/70" : ""}`}
                    style={{ minWidth: 100 }}
                  >
                    {fmtMonthShort(mo)}
                    {mo === CUR && <span className="ml-1 inline-block w-1 h-1 bg-[var(--ink)] rounded-full align-middle" title="As-at month" />}
                    {future && <Lock size={8} className="ml-1 inline align-middle opacity-60" />}
                  </th>
                );
              })}
              <th className="px-3 py-2 eyebrow text-right border-b border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] sticky right-0 z-20" style={{ minWidth: 110, boxShadow: "-1px 0 0 var(--rule-strong)" }}>Row total</th>
            </tr>
            {/* Status row · click a header cell to confirm invoicing for that month */}
            <tr>
              <th
                colSpan={2}
                className="sticky left-0 z-20 bg-[var(--paper-soft)] eyebrow text-right px-3 py-1 text-[10px] normal-case tracking-[0.06em] text-[var(--soft-fg)] border-b border-r border-[var(--rule-strong)]"
                style={{ minWidth: FROZEN_W }}
              >
                Status
              </th>
              <th colSpan={2} className="border-b border-r border-[var(--rule)] bg-[var(--paper-soft)]" />
              {months.map((mo) => {
                const conf = isConfirmed(mo);
                const any = hasAny(mo);
                const past = isPast(mo);
                const future = isFuture(mo);
                return (
                  <th key={mo} className={`p-0.5 border-b border-[var(--rule)]/60 ${past && !conf ? "bg-[var(--paper-soft)]/40" : ""} ${future ? "bg-[#FBF7EF]" : ""}`} style={{ minWidth: 100 }}>
                    <button
                      type="button"
                      onClick={() => onConfirmMonth?.(mo, !conf)}
                      disabled={future || (!any && !conf)}
                      className={`w-full py-1 text-[10px] font-mono-num tracked-tight uppercase inline-flex items-center justify-center gap-1 border transition-colors disabled:cursor-not-allowed ${
                        future
                          ? "bg-transparent border-transparent text-[#7C5A16] opacity-100"
                          : conf
                            ? "bg-[#E6F1E2] border-[#2C4A38]/60 text-[#2C4A38] hover:bg-[#D8E7D2]"
                            : any
                              ? "bg-[#F0EFE8] border-[var(--rule-strong)] text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]"
                              : "bg-transparent border-transparent text-[var(--rule-strong)] disabled:opacity-30"
                      }`}
                      title={
                        future ? futureWhy(mo) :
                        conf ? "Confirmed — click to revert to draft" :
                        any ? "Draft · click to confirm invoicing for this month" :
                        "No invoiced amounts yet"
                      }
                      data-testid={`invoice-status-${mo}`}
                    >
                      {future ? (any ? (<><Lock size={8} /> after as-at</>) : (<>locked</>))
                        : conf ? (<><Check size={9} /> confirmed</>)
                        : any ? (<>draft</>)
                        : (<>—</>)}
                    </button>
                  </th>
                );
              })}
              <th className="border-b border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] sticky right-0 z-20" style={{ boxShadow: "-1px 0 0 var(--rule-strong)" }} />
            </tr>
          </thead>
          <tbody>
            {rowIdxs.map((idx) => {
              const s = scopes[idx];
              return (
              <tr key={s.id || idx} className={`border-b border-[var(--rule)] ${s.isSubConsultant ? "bg-[#F6F4EE]" : ""}`} data-testid={`invoiced-row-${idx}`}>
                <td className={`sticky left-0 z-10 px-3 py-2 border-r border-[var(--rule)] ${s.isSubConsultant ? "bg-[#F6F4EE]" : "bg-white"}`} style={{ width: W_STAGE, minWidth: W_STAGE }}>
                  <div className="text-xs">{rowLabel(s)}</div>
                </td>
                <td className={`sticky z-10 px-2 py-2 text-xs text-[var(--soft-fg)] border-r border-[var(--rule-strong)] ${s.isSubConsultant ? "bg-[#F6F4EE]" : "bg-white"} ${s.feeType === "time_charge" ? "text-[#4a5a7a]" : ""}`} style={{ left: W_STAGE, width: W_TYPE, minWidth: W_TYPE }}>
                  {feeTypeLabel(s)}
                </td>
                <td className="p-0 border-r border-[var(--rule)] bg-white" style={{ width: W_CONTRACT, minWidth: W_CONTRACT }}>
                  <MoneyCell
                    value={s.contractValue ?? 0}
                    onChange={(v) => onScopeChange?.(idx, { contractValue: v })}
                    allowFlag={false}
                    testid={`invoiced-${idx}-contractvalue`}
                  />
                </td>
                <td className="p-0 border-r border-[var(--rule)] bg-white" style={{ width: W_NOTE, minWidth: W_NOTE }}>
                  <input
                    type="text"
                    value={s.invoiceNote || ""}
                    onChange={(e) => onScopeChange?.(idx, { invoiceNote: e.target.value })}
                    placeholder="invoicing note…"
                    className="w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white placeholder:text-[var(--rule-strong)]"
                    data-testid={`invoiced-${idx}-note`}
                  />
                </td>
                {months.map((mo) => {
                  const conf = isConfirmed(mo);
                  const past = isPast(mo);
                  const future = isFuture(mo);
                  const d = delta(s, mo);
                  const stored = Number(s.invoiced?.[mo] || 0);
                  return (
                    <td key={mo} className={`p-0 border-r border-[var(--rule)]/40 relative ${past && !conf ? "bg-[var(--paper-soft)]/40" : ""} ${conf ? "bg-[#F3F7F1]" : ""} ${future ? "bg-[#FBF7EF]" : ""}`} style={{ minWidth: 100 }}>
                      {future ? (
                        <div
                          className={`w-full px-2 py-2 font-mono-num text-xs text-right inline-flex items-center justify-end gap-1 ${stored !== 0 ? "text-[#7C5A16] border-l-2 border-[#B98A22]" : "text-[var(--rule-strong)]"}`}
                          data-testid={`invoiced-${idx}-${mo}`}
                          title={futureWhy(mo) + (stored !== 0 ? " This figure predates the guard and has been left exactly as it was — it is under review." : "")}
                        >
                          <Lock size={9} className="opacity-70" />
                          {stored !== 0 ? fmtMoney(stored) : "—"}
                        </div>
                      ) : conf ? (
                        <div className="w-full px-2 py-2 font-mono-num text-xs text-right text-[#2C4A38] border-l-2 border-[#2C4A38]/70 inline-flex items-center justify-end gap-1" data-testid={`invoiced-${idx}-${mo}`} title="Confirmed — revert via the header to edit">
                          <Lock size={9} className="opacity-70" />
                          {Number(s.invoiced?.[mo] || 0) !== 0 ? fmtMoney(s.invoiced[mo]) : <span className="text-[var(--rule-strong)]">—</span>}
                        </div>
                      ) : (
                        <RatioCell
                          cv={Number(s.contractValue) || 0}
                          value={s.invoiced?.[mo] ?? 0}
                          ratio={s.invoicedRatios?.[mo]}
                          onChange={(v, ratioStr) => onScopeInvoiced(idx, mo, v, ratioStr)}
                          negativeDefault={!!s.isSubConsultant}
                          testid={`invoiced-${idx}-${mo}`}
                        />
                      )}
                      {d !== null && !future && (
                        <div
                          className="px-1 pb-0.5 text-right leading-none"
                          data-testid={`invoice-delta-${idx}-${mo}`}
                          title={`Invoiced ${fmtMoney(Number(s.invoiced?.[mo] || 0))} · Forecast ${fmtMoney(Number(s.revenue?.[mo] || 0))}${d > 0 ? " (invoiced is over)" : " (invoiced is under)"}`}
                        >
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-mono-num text-[#6B2F2A] whitespace-nowrap">
                            <AlertTriangle size={8} />
                            {d > 0 ? "+" : "−"}{fmtMoney(Math.abs(d), { compact: true })} <span className="opacity-70">vs fc</span>
                          </span>
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 font-mono-num text-right border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] sticky right-0 z-10" style={{ boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid={`invoiced-rowtotal-${idx}`}>
                  {rowTotal(s.invoiced) ? fmtMoney(rowTotal(s.invoiced)) : "—"}
                </td>
              </tr>
              );
            })}
            <tr className="bg-[var(--paper-soft)]">
              <td colSpan={2} className="sticky left-0 z-10 bg-[var(--paper-soft)] px-3 py-2 eyebrow border-r border-[var(--rule-strong)] border-t border-[var(--rule-strong)]" style={{ minWidth: FROZEN_W }}>Invoiced total</td>
              <td className="px-2 py-2 font-mono-num text-right border-t border-r border-[var(--rule)] bg-[var(--paper-soft)]" data-testid="invoiced-contract-total">
                {contractTotal ? fmtMoney(contractTotal) : "—"}
              </td>
              <td className="border-t border-r border-[var(--rule)] bg-[var(--paper-soft)]" />
              {months.map((mo) => {
                const past = isPast(mo);
                return (
                  <td key={mo} className={`px-2 py-2 font-mono-num text-right border-t border-[var(--rule-strong)] ${past ? "bg-[var(--paper-soft)]/90" : ""}`} data-testid={`invoiced-total-${mo}`}>
                    {monthTotal(mo) ? fmtMoney(monthTotal(mo)) : "—"}
                  </td>
                );
              })}
              <td className="px-3 py-2 font-mono-num text-right border-t border-l border-[var(--rule-strong)] bg-[var(--ink)] text-white sticky right-0 z-10" style={{ boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid="invoiced-grand-total">
                {fmtMoney(grand)}
                {hasAltCurrency(project) && (
                  <div
                    className="text-[9px] opacity-80"
                    data-testid="invoiced-grand-total-alt"
                    title={`Locked rate 1 NZD = ${fxRound(lockedRateOf(project))} ${project.currency} — invoiced figures never move with the market`}
                  >
                    {fmtAltValue(altAmount(grand, project), project.currency)} · locked
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </DragScrollContainer>
      <div className="border-t border-[var(--rule)] px-3 py-2 text-[10px] font-mono-num text-[var(--soft-fg)]" data-testid="invoice-as-at-note">
        <Lock size={8} className="inline align-middle mr-1 opacity-70" />
        Months after the as-at month ({fmtMonthShort(CUR)}) are locked — invoicing only ever records what has
        already been billed. Any figure already stored beyond it stays visible in amber, read-only, and untouched.
      </div>
    </div>
  );
}
